import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMedal, faRobot, faDice } from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Activity, DailyChallenge, PlayerStats } from '../types/activity';
import PlayerStatsTooltip from './PlayerStatsTooltip';
import ActivityChip from './ActivityChip';
import { isSameLocalDay } from '../utils/dateUtils';
import { sumPoints } from '../utils/scoring';
import { formatPoints, sumMinutes } from '../utils/scoring';
import { clampTooltipX } from '../utils/tooltipClamp';
import { useScrollDismiss } from '../hooks/useScrollDismiss';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAYS_FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function rewardLabelShort(ch: DailyChallenge): string {
  if (ch.reward_type === 'flat') return `+${ch.reward_value} pts`;
  if (ch.type === 'triathlon') return '3× pts';
  return '2× pts (max 150)';
}

function challengeIcon(_type: string): IconDefinition {
  return faMedal;
}

interface Props {
  day: Date;
  dayIndex: number;
  myActivities: Activity[];
  opponentActivities: Activity[];
  today: boolean;
  isCenter: boolean;
  challenge: DailyChallenge | null;
  opponentAvatar?: string | null;
  opponentIsBot?: boolean;
  opponentName?: string;
  opponentId?: number;
  myName?: string;
  myAvatar?: string | null;
  myIsBot?: boolean;
  myStats?: PlayerStats | null | false;
  opponentStats?: PlayerStats | null | false;
  hidePoints?: boolean;
  hideOpponentPoints?: boolean;
  hideSpin?: boolean;
  disableChipTooltips?: boolean;
  hideOpponentTooltips?: boolean;
  hideChallengeEarned?: boolean;
  workoutDeadline?: string;
  maxSpinDays?: number;
  spinStartDate?: string | null;
  spinItemId?: number | null;
  myId?: number;
  onNavigateToPlayerProfile?: (playerId: number) => void;
}

const SHORT_SPIN = ['Run','Zone3','Swim','Rest','Yoga','Ride','Trail','HR+','New!','Hike'];

export default function CalendarDay({ day, dayIndex, myActivities, opponentActivities, today, isCenter, challenge, workoutDeadline, opponentAvatar, opponentIsBot, opponentName = 'Opponent', opponentId, myName = 'You', myAvatar, myIsBot = false, myStats, opponentStats, hidePoints = false, hideOpponentPoints = false, hideSpin = false, disableChipTooltips = false, hideOpponentTooltips = false, hideChallengeEarned = false, maxSpinDays = 1, spinStartDate = null, spinItemId = null, myId, onNavigateToPlayerProfile }: Props) {
  const { token } = useAuth();
  // Day relationship to today
  const todayMidnight = new Date(); todayMidnight.setHours(0,0,0,0);
  const dayMidnight   = new Date(day); dayMidnight.setHours(0,0,0,0);
  const diffDays = Math.round((dayMidnight.getTime() - todayMidnight.getTime()) / 86400000);

  // Spin-Bereich: rel. zu spinStartDate (Aktivierungsdatum) statt today
  const spinBase = spinStartDate ? new Date(spinStartDate + 'T00:00:00') : todayMidnight;
  const spinBaseMidnight = new Date(spinBase); spinBaseMidnight.setHours(0,0,0,0);
  const daysSinceSpin = Math.round((dayMidnight.getTime() - spinBaseMidnight.getTime()) / 86400000);

  // Morgen ist IMMER spinnbar (fuer alle User)
  const isTomorrowDay = diffDays === 1;
  // Zusätzliche Tage nur wenn Spin hard! Item aktiv und innerhalb des Fensters
  const isSpinWindowDay = spinStartDate
    ? (daysSinceSpin >= 2 && daysSinceSpin <= maxSpinDays)
    : (diffDays >= 2 && diffDays <= maxSpinDays);
  const isFutureDay = diffDays > 0 && (isTomorrowDay || isSpinWindowDay);
  const isHiddenDay = diffDays > maxSpinDays && !isTomorrowDay;
  // Spin hard! Item: Alle future days sind spinnbar, nicht nur morgen
  const isSpinnableDay = isFutureDay;

  // Bot-Aktivitäten für zukünftige Tage nicht anzeigen
  const myDay  = myActivities.filter((a) => isSameLocalDay(a.started_at_utc, day) && !((isFutureDay || isHiddenDay) && myIsBot));
  const earnedChallengePts = myDay.reduce((sum, a) => sum + (Number(a.challenge_bonus_points) || 0), 0);
  const oppDay = opponentActivities.filter((a) => isSameLocalDay(a.started_at_utc, day) && !((isFutureDay || isHiddenDay) && opponentIsBot));
  const dayPoints   = sumPoints(myDay);
  const dayMinutes  = sumMinutes(myDay);
  const oppMinutes  = sumMinutes(oppDay);

  // Spin state – DB-basiert über player_ui_state Tabelle
  const spinDateKey = `${day.getFullYear()}-${String(day.getMonth()+1).padStart(2,'0')}-${String(day.getDate()).padStart(2,'0')}`;
  const spinStateKey = `spin_${spinDateKey}`;

  const [spinState, setSpinState] = useState<'idle'|'spinning'|'done'>('idle');
  const [spunChallenge, setSpunChallenge] = useState<DailyChallenge | null>(null);
  const [spinTitle, setSpinTitle] = useState('');
  const spinInterval = useRef<NodeJS.Timeout | null>(null);

  // DB-Check: Prüfe ob Spin für dieses Datum bereits gespeichert ist
  useEffect(() => {
    if (!token || !isFutureDay) return;
    api.get('/player/me/ui-states', token).then((data: any) => {
      if (data?.states?.[spinStateKey]) {
        setSpinState('done');
        // Challenge-Daten aus localStorage-Cache (temporär, bis Challenge-Daten auch in DB sind)
        const cached = localStorage.getItem(`nww_spin_data_${spinDateKey}`);
        if (cached) {
          try { setSpunChallenge(JSON.parse(cached)); } catch {}
        }
      }
    }).catch(() => {});
  }, [token, isFutureDay, spinStateKey, spinDateKey]);

  // If spin was done but no data was stored (challenge wasn't assigned yet), use challenge prop or retry
  useEffect(() => {
    if (spinState !== 'done' || spunChallenge !== null || !isFutureDay) return;
    // Use challenge prop if available (assigned via weekly challenges)
    if (challenge) {
      setSpunChallenge(challenge);
      localStorage.setItem(`nww_spin_data_${spinDateKey}`, JSON.stringify(challenge));
      return;
    }
    // Fallback: fetch tomorrow's challenge from API (only works for tomorrow)
    if (token && isTomorrowDay) {
      api.get('/challenges/tomorrow', token).then((res: any) => {
        const c = res?.challenge ?? null;
        if (c) {
          setSpunChallenge(c);
          localStorage.setItem(`nww_spin_data_${spinDateKey}`, JSON.stringify(c));
        }
      }).catch(() => {});
    }
  }, [spinState, spunChallenge, token, isFutureDay, isTomorrowDay, challenge, spinDateKey]);

  async function handleDaySpin() {
    if (!isSpinnableDay || spinState !== 'idle') return;
    setSpinState('spinning');
    let idx = 0;
    spinInterval.current = setInterval(() => {
      setSpinTitle(SHORT_SPIN[idx % SHORT_SPIN.length]); idx++;
    }, 80);
    // Use the challenge prop directly if available (already pre-assigned), otherwise fetch for this specific date
    let fetched: DailyChallenge | null = challenge ?? null;
    if (!fetched && token) {
      try {
        // Challenge fuer genau dieses Datum zuweisen und laden
        const res = await api.post('/challenges/spin/assign', { date: spinDateKey }, token);
        fetched = res?.challenge ?? null;
      } catch {
        // Fallback: Challenge aus Cache oder /challenges/tomorrow
        const cached = localStorage.getItem(`nww_spin_data_${spinDateKey}`);
        if (cached) { try { fetched = JSON.parse(cached); } catch {} }
        if (!fetched) {
          try { const res = await api.get('/challenges/tomorrow', token); fetched = res?.challenge ?? null; }
          catch {}
        }
      }
    }
    await new Promise(r => setTimeout(r, 1600));
    if (spinInterval.current) { clearInterval(spinInterval.current); spinInterval.current = null; }
    const delays = [120, 160, 220, 300];
    for (const d of delays) {
      await new Promise(r => setTimeout(r, d));
      setSpinTitle(SHORT_SPIN[idx % SHORT_SPIN.length]); idx++;
    }
    setSpunChallenge(fetched);
    // API: Spin-Item aktivieren (statt /challenges/spin/seen)
    if (token && spinItemId) {
      api.post('/player/me/items/use', { user_item_id: spinItemId }, token).catch(() => {
        // Fallback: Falls Item bereits geloescht, spin-Seen trotzdem speichern
        api.post('/challenges/spin/seen', { date: spinDateKey }, token);
      });
    } else if (token) {
      // Kein Item vorhanden (Standard-Spin fuer naechsten Tag)
      api.post('/challenges/spin/seen', { date: spinDateKey }, token);
    }
    // Cache: Challenge-Daten zwischenspeichern (bis Challenge-Daten auch in DB sind)
    localStorage.setItem(`nww_spin_data_${spinDateKey}`, JSON.stringify(fetched));
    setSpinState('done');
  }

  useEffect(() => () => { if (spinInterval.current) clearInterval(spinInterval.current); }, []);

  const [showChallengeTip, setShowChallengeTip] = useState<{ x: number; y: number } | null>(null);
  useScrollDismiss(showChallengeTip, setShowChallengeTip);
  const isTouch = useRef(typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches);

  // Outside-click: Challenge-Tooltip schließen (nur Touch)
  useEffect(() => {
    if (!showChallengeTip || !isTouch.current) return;
    const handler = () => setShowChallengeTip(null);
    document.addEventListener('click', handler, { once: true });
    return () => document.removeEventListener('click', handler);
  }, [showChallengeTip]);

  const [tooltip, setTooltip] = useState<{ which: 'mine' | 'opponent'; x: number; y: number } | null>(null);
  useScrollDismiss(tooltip, setTooltip);
  const [fetchedOppStats, setFetchedOppStats] = useState<PlayerStats | null | false>(null);
  const oppStatsRequested = useRef(false);

  // Countdown fuer heutige Challenge
  // Am Sonntag endet der Tag um workout_deadline_utc (18:00 UTC), sonst um Mitternacht UTC
  const [countdown, setCountdown] = useState('');
  useEffect(() => {
    if (!today || isFutureDay || !challenge) return;
    const tick = () => {
      const now = Date.now();
      let endMs: number;
      if (workoutDeadline) {
        const deadlineMs = new Date(workoutDeadline.replace(' ', 'T') + 'Z').getTime();
        // Use deadline if it's earlier than midnight (i.e. Sunday cutoff)
        const tomorrow = new Date();
        tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
        tomorrow.setUTCHours(0, 0, 0, 0);
        endMs = deadlineMs < tomorrow.getTime() ? deadlineMs : tomorrow.getTime();
      } else {
        const tomorrow = new Date();
        tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
        tomorrow.setUTCHours(0, 0, 0, 0);
        endMs = tomorrow.getTime();
      }
      const diff = Math.max(0, Math.floor((endMs - now) / 1000));
      if (diff <= 0) { setCountdown('0:00'); return; }
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setCountdown(`${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [today, isFutureDay, challenge, workoutDeadline]);

  const effectiveOppStats = opponentStats ?? fetchedOppStats;
  const effectiveMyStats = myStats ?? null;

  function showTooltip(e: React.MouseEvent<Element>, which: 'mine' | 'opponent') {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({ which, x: clampTooltipX(rect.left + rect.width / 2, 240, true), y: rect.bottom + 10 });
    // Load opponent stats on first hover if not yet available
    if (which === 'opponent' && !effectiveOppStats && !oppStatsRequested.current && opponentId) {
      oppStatsRequested.current = true;
      api.get(`/player/${opponentId}/stats`, token!).then((data: any) => {
        if (data && data.wins !== undefined) setFetchedOppStats(data as PlayerStats);
        else setFetchedOppStats(false);
      }).catch(() => setFetchedOppStats(false));
    }
  }
  function hideTooltip() { setTooltip(null); }
  function toggleTooltip(e: React.MouseEvent<Element>, which: 'mine' | 'opponent') {
    e.stopPropagation();
    if (tooltip?.which === which) { setTooltip(null); }
    else { showTooltip(e, which); }
  }


  return (
    <div className={`calendar-day${today ? ' calendar-day--today' : ''}${isCenter ? ' calendar-day--center' : ''}`}>
      <div className="calendar-day-header">
        <span className="day-name">{DAYS[dayIndex]}</span>
        <span className="day-date">{day.getDate()}.{day.getMonth() + 1}.</span>
      </div>

      {/* Challenge or Spin button:
          - Past/today: show challenge directly from weekChallenges prop
          - Tomorrow (diffDays=1): spin flow to reveal challenge
          - Days 2+ beyond maxSpinDays: greyed-out, non-clickable spin button */}
      {!isFutureDay && !isHiddenDay && challenge ? (
        today ? (
          // Heute: vollständige Challenge-Info direkt im Kalender
          <div className="day-challenge day-challenge--today">
            <span className="day-challenge-label">Daily Challenge</span>
            <div className="day-challenge-title-row">
              <span className="day-challenge-icon"><FontAwesomeIcon icon={challengeIcon(challenge.type)} /></span>
              <span className="day-challenge-title">{challenge.title}</span>
            </div>
            <p className="day-challenge-desc">{challenge.code === 'long_workout' ? 'Your strongest activity lasting more than 50 minutes earns double points (no pause!).' : challenge.description}</p>
            <div className="day-challenge-bottom">
              {countdown && <span className="day-challenge-countdown">{countdown}</span>}
              <span className="day-challenge-reward">{rewardLabelShort(challenge)}</span>
            </div>
          </div>
        ) : (
          // Vergangene Tage: erweitertes Layout mit verdienten Challenge-Punkten
          <div className="day-challenge day-challenge--past">
            <span className="day-challenge-label">Daily Challenge</span>
            <div className="day-challenge-title-row">
              <span className="day-challenge-icon"><FontAwesomeIcon icon={challengeIcon(challenge.type)} /></span>
              <span className="day-challenge-title">{challenge.title}</span>
            </div>
            <p className="day-challenge-desc">{challenge.code === 'long_workout' ? 'Your strongest activity lasting more than 50 minutes earns double points (no pause!).' : challenge.description}</p>
            <div className="day-challenge-bottom">
              {!hideChallengeEarned && (earnedChallengePts > 0
                ? <span className="day-challenge-earned">+{earnedChallengePts} pts</span>
                : <span className="day-challenge-earned day-challenge-earned--none">No bonus</span>
              )}
            </div>
          </div>
        )
      ) : isFutureDay && !hideSpin ? (
        spinState === 'spinning' ? (
          <div className="day-challenge day-challenge--spinning">
            <span className="day-challenge-icon"><FontAwesomeIcon icon={faDice} /></span>
            <span className="day-challenge-title">{spinTitle}</span>
          </div>
        ) : spinState === 'done' ? (
          spunChallenge ? (
            <div className="day-challenge day-challenge--tomorrow">
              <span className="day-challenge-label">{DAYS_FULL[dayIndex]} Challenge</span>
              <div className="day-challenge-title-row">
                <span className="day-challenge-icon"><FontAwesomeIcon icon={challengeIcon(spunChallenge.type)} /></span>
                <span className="day-challenge-title">{spunChallenge.title}</span>
              </div>
              <p className="day-challenge-desc">{spunChallenge.code === 'long_workout' ? 'Your strongest activity lasting more than 50 minutes earns double points (no pause!).' : spunChallenge.description}</p>
              <div className="day-challenge-bottom">
                <span className="day-challenge-reward">{rewardLabelShort(spunChallenge)}</span>
              </div>
            </div>
          ) : (
            <div className="day-challenge day-challenge--spun">
              <span className="day-challenge-icon"><FontAwesomeIcon icon={faDice} /></span>
              <span className="day-challenge-title">Soon…</span>
            </div>
          )
        ) : (
          <button
            className={`day-spin-btn${isSpinnableDay ? ' day-spin-btn--active' : ' day-spin-btn--disabled'}`}
            onClick={handleDaySpin}
            disabled={!isSpinnableDay}
            title={isSpinnableDay ? 'Reveal this day\'s challenge' : 'Available later this week'}
          >
            <FontAwesomeIcon icon={faDice} />
            <span>Spin</span>
          </button>
        )
      ) : isHiddenDay && !hideSpin ? (
        <button
          className="day-spin-btn day-spin-btn--disabled"
          disabled
          title="Available later this week"
        >
          <FontAwesomeIcon icon={faDice} />
          <span>Spin</span>
        </button>
      ) : null}

      <div className="day-columns">
        <div className="day-col day-col--mine">
          <div className="day-col-header">
            {myIsBot ? (
              <span
                onMouseEnter={(e) => showTooltip(e, 'mine')}
                onMouseLeave={hideTooltip}
                onClick={(e) => toggleTooltip(e, 'mine')}
              >
                <FontAwesomeIcon icon={faRobot} className="day-col-bot" style={{ color: '#c53616' }} />
              </span>
            ) : myAvatar ? (
              <img
                src={myAvatar} alt={myName} className="day-col-avatar"
                style={{ cursor: onNavigateToPlayerProfile && myId ? 'pointer' : undefined }}
                onMouseEnter={(e) => showTooltip(e, 'mine')}
                onMouseLeave={hideTooltip}
                onClick={(e) => { e.stopPropagation(); if (onNavigateToPlayerProfile && myId) onNavigateToPlayerProfile(myId); else toggleTooltip(e, 'mine'); }}
              />
            ) : null}
          </div>
          {myDay.length === 0
            ? <div className="day-empty">—</div>
            : myDay.map((a) => <ActivityChip key={a.id} activity={a} variant="mine" hideTooltip={disableChipTooltips} challenge={challenge} />)
          }
          {(hidePoints && !myDay.some(a => a.sport_type === 'BadgeReward')) ? (
            myDay.length > 0 && (
              <div className="day-col-total">
                <span className="day-col-total-minutes">{dayMinutes}m</span>
                <span className="day-col-total-value">?</span>
              </div>
            )
          ) : (
            dayPoints > 0 && (
              <div className="day-col-total">
                <span className="day-col-total-minutes">{dayMinutes}m</span>
                <span className="day-col-total-value">{formatPoints(dayPoints)}</span>
              </div>
            )
          )}
        </div>

        <div className="day-col day-col--opponent">
          <div className="day-col-header">
            {opponentAvatar && !opponentIsBot ? (
              <img
                src={opponentAvatar} alt={opponentName} className="day-col-avatar"
                style={{ cursor: onNavigateToPlayerProfile && opponentId ? 'pointer' : undefined }}
                onMouseEnter={(e) => showTooltip(e, 'opponent')}
                onMouseLeave={hideTooltip}
                onClick={(e) => { e.stopPropagation(); if (onNavigateToPlayerProfile && opponentId) onNavigateToPlayerProfile(opponentId); else toggleTooltip(e, 'opponent'); }}
              />
            ) : opponentIsBot && (
              <span
                onMouseEnter={(e) => showTooltip(e, 'opponent')}
                onMouseLeave={hideTooltip}
                onClick={(e) => toggleTooltip(e, 'opponent')}
              >
                <FontAwesomeIcon icon={faRobot} className="day-col-bot" />
              </span>
            )}
          </div>
          {oppDay.length === 0
            ? <div className="day-empty">—</div>
            : oppDay.map((a, j) => (
                <ActivityChip key={j} activity={a} variant="opponent" showOppPoints={(!hidePoints && !hideOpponentPoints) || a.sport_type === 'Consolation' || a.sport_type === 'BadgeReward' || !!a.revealed} hideTooltip={(disableChipTooltips || hideOpponentTooltips) && !a.revealed && a.sport_type !== 'BadgeReward'} challenge={challenge} />
              ))
          }
          {oppDay.length > 0 && (
            <div className="day-col-total day-col-total--opp">
              <span className="day-col-total-minutes day-col-total-minutes--opp">{oppMinutes}m</span>
              <span className="day-col-total-value day-col-total-value--opp">{(hidePoints || hideOpponentPoints) && !oppDay.some(a => a.sport_type === 'BadgeReward') ? '?' : formatPoints(sumPoints(oppDay))}</span>
            </div>
          )}
        </div>
      </div>

      {tooltip && createPortal(
        <div
          style={{ position: 'fixed', top: tooltip.y, left: tooltip.x, transform: 'translateX(-50%)', zIndex: 9999, pointerEvents: 'none' }}
        >
          {tooltip.which === 'mine'
            ? <PlayerStatsTooltip stats={effectiveMyStats} name={myName} isMine={true} />
            : <PlayerStatsTooltip stats={effectiveOppStats} name={opponentName} isMine={false} />
          }
        </div>,
        document.body
      )}

      {showChallengeTip && (challenge || spunChallenge) && createPortal(
        <div
          className="app-tooltip day-challenge-tip"
          style={{ position: 'fixed', top: showChallengeTip.y, left: showChallengeTip.x, transform: 'translateX(-50%)', zIndex: 9999 }}
          onClick={(e) => { e.stopPropagation(); setShowChallengeTip(null); }}
        >
          {(challenge || spunChallenge)?.description}
        </div>,
        document.body
      )}
    </div>
  );
}
